import React, { useState, useContext, useRef, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { MyContext, ListContext } from "./MyContext";
import COLORS from "./styleConstants";

// import AuthHelperMethods from "./AuthHelperMethods";
import Auth from "./Auth";
import { getPaymentErrorMessage } from "./paymentErrorMessages";

export const AccountPaymentMethod = ({ onCancel, onSaved, defaultMakeDefault = false }) => {

  const getPublicStripeKey = async () => {
    const result = await Auth.api("payments/getPublicStripeKey", "get");
    console.log("gettingi public stripe key");
    return result.data.publicKey;
  };

  const getFarmAccountId = async () => {
    const result = await Auth.api("payments/getFarmAccountId", "get");
    //console.log(result.data);
    return result.data.stripe_account_id;
  };

  const [stripePromise] = useState(() => getPublicStripeKey().then((key) =>
    getFarmAccountId().then((farm) =>
      loadStripe(key, {
        stripeAccount: farm,
      }),
    ),
  ));

  return (
    <Elements stripe={stripePromise}>
      <CheckoutForm onCancel={onCancel} onSaved={onSaved} defaultMakeDefault={defaultMakeDefault} />
    </Elements>
  );
};

const CheckoutForm = ({ onCancel, onSaved, defaultMakeDefault }) => {
  const { dispatch } = useContext(MyContext);
  const { listState } = useContext(ListContext);

  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [makeDefault, setMakeDefault] = useState(defaultMakeDefault);
  const defaultChoiceChangedRef = useRef(false);
  const submittingRef = useRef(false);
  const paymentMethodRef = useRef(null);
  const setupIntentRef = useRef(null);
  const idempotencyKeyRef = useRef(null);

  useEffect(() => {
    if (!defaultChoiceChangedRef.current && !idempotencyKeyRef.current) {
      setMakeDefault(defaultMakeDefault);
    }
  }, [defaultMakeDefault]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || submittingRef.current) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    submittingRef.current = true;
    setProcessing(true);
    setErrorMessage("");
    try {
      dispatch({
        type: "FETCH_INIT",
      });
      if (!paymentMethodRef.current) {
        const payload = await stripe.createPaymentMethod({
          type: "card",
          card: elements.getElement(CardElement),
        });
        if (payload.error) throw new Error(payload.error.message);
        paymentMethodRef.current = payload.paymentMethod.id;
      }
      if (!idempotencyKeyRef.current) idempotencyKeyRef.current = uuidv4();
      if (!setupIntentRef.current) {
        const result = await Auth.api("payments/createSetupIntent", "post", {
          person_id: listState.accountHolder.id,
          payment_method_id: paymentMethodRef.current,
          idempotency_key: idempotencyKeyRef.current,
          make_default: makeDefault,
        });
        setupIntentRef.current = result?.data?.setupIntent;
      }
      let setupIntent = setupIntentRef.current;
      if (setupIntent?.status === "requires_action") {
        const payload = await stripe.confirmCardSetup(setupIntent.client_secret);
        if (payload.error) throw new Error(payload.error.message);
        setupIntent = payload.setupIntent;
        setupIntentRef.current = setupIntent;
      }
      if (setupIntent?.status !== "succeeded") {
        throw new Error("The card setup was not completed. Please check its status before trying again.");
      }
      dispatch({
        type: "FETCH_SUCCESS",
      });
      alert("payment method added");
      onSaved?.();
      onCancel?.();
    } catch (error) {
      console.log("Stripe error object:", error);
      setErrorMessage(error.response
        ? getPaymentErrorMessage(error)
        : error.message || "Unable to save this card.");
      dispatch({
        type: "FETCH_ERROR",
      });
    } finally {
      submittingRef.current = false;
      setProcessing(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <div
          className="addPaymentMethodAccount"
          style={{
            width: "100%",
            maxWidth: 520,
            margin: "16px auto 24px",
            padding: 18,
            border: "1px solid #e2e5e9",
            borderRadius: 6,
            backgroundColor: "#fafbfc",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              marginBottom: 14,
              color: COLORS.textDark,
              fontSize: 14,
              fontWeight: "bold",
            }}
          >
            Add payment method
          </div>
          <div
            style={{
              padding: "12px 10px",
              border: "1px solid #d7dce1",
              borderRadius: 4,
              backgroundColor: "white",
            }}
          >
            <CardElement
              options={{ disabled: processing }}
              onChange={() => {
                if (!submittingRef.current) {
                  paymentMethodRef.current = null;
                  setupIntentRef.current = null;
                  idempotencyKeyRef.current = null;
                  setErrorMessage("");
                }
              }}
            />
          </div>
          <label
            style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}
          >
            <input
              type="checkbox"
              checked={makeDefault}
              onChange={(event) => {
                defaultChoiceChangedRef.current = true;
                setMakeDefault(event.target.checked);
              }}
              disabled={processing || Boolean(idempotencyKeyRef.current)}
            />
            Make this card the default
          </label>
          {errorMessage && (
            <div role="alert" className="message sr-field-error">
              {errorMessage}
            </div>
          )}
          <div
            style={{
              display: "flex",
              gap: 8,
              marginTop: 10,
              justifyContent: "flex-end",
            }}
          >
            <button
              type="button"
              onClick={onCancel}
              disabled={processing}
              style={{
                background: COLORS.blue,
                border: "none",
                borderRadius: 4,
                color: "white",
                cursor: "pointer",
                padding: "6px 12px",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={processing || !stripe || !elements}
              style={{
                background: COLORS.orange,
                border: "none",
                borderRadius: 4,
                color: "white",
                cursor: "pointer",
                padding: "6px 12px",
              }}
            >
              {processing ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </form>
    </>
  );
};
